import React, { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  Input,
  Textarea,
  useMediaQuery,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SectionTitle, Text } from '../../../components';
import { InputProps } from '../../../models/Form';
import { TextType } from '../../../models/Text';

const FORMSPREE_ENDPOINT =
  'https://formspree.io/f/xoeaezve';

const EmailMeForm = () => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const [checkbox, setCheckbox] = useState(false);

  const formData = useMemo<InputProps[]>(
    () => [
      {
        order: 1,
        mobileOrder: 1,
        gridArea: 'firstName',
        label: 'homepage.contactUs.form.labels.firstName',
        formKey: 'firstName',
        requiredLabel: true,
      },
      {
        order: 2,
        mobileOrder: 3,
        gridArea: 'businessName',
        label: 'homepage.contactUs.form.labels.businessName',
        formKey: 'businessName',
      },
      {
        order: 3,
        mobileOrder: 2,
        gridArea: 'lastName',
        label: 'homepage.contactUs.form.labels.lastName',
        formKey: 'lastName',
        requiredLabel: true,
      },
      {
        order: 4,
        mobileOrder: 4,
        gridArea: 'businessEmail',
        label: 'homepage.contactUs.form.labels.businessEmail',
        formKey: 'businessEmail',
        requiredLabel: true,
        inputType: 'email',
      },
      {
        order: 5,
        mobileOrder: 5,
        gridArea: 'message',
        label: 'homepage.contactUs.form.labels.message',
        formKey: 'message',
        requiredLabel: true,
        multiline: true,
      },
    ],
    []
  );

  const formInputs = useMemo<InputProps[]>(
    () =>
      [...formData].sort((a, b) =>
        isMobile
          ? a.mobileOrder - b.mobileOrder
          : a.order - b.order
      ),
    [formData, isMobile]
  );

  const renderFormInput = useCallback(
    ({
      order,
      label,
      formKey,
      inputType,
      requiredLabel = false,
      multiline = false,
    }: InputProps) => (
      <FormControl
        key={label}
        width={{
          base: '100%',
          md: multiline ? '100%' : '50%',
        }}
      >
        <Flex
          flex={1}
          flexDirection={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent={
            isMobile ? 'center' : 'space-between'
          }
          marginBottom={{
            base: '10px',
            md: '40px',
          }}
          marginLeft={{
            base: 0,
            md: order % 2 === 0 ? '20px' : 0,
            lg: order % 2 === 0 ? '40px' : 0,
          }}
        >
          <Box
            marginBottom={{
              base: '10px',
              md: 0,
            }}
            width={{
              base: '100%',
              md: multiline ? '12.5%' : '25%',
            }}
          >
            <Text
              type={{
                base: TextType.Inter12Light,
                md: TextType.Inter14Light,
                lg: TextType.Inter14Light,
              }}
              color="gray.500"
            >
              {`${t(label)}${requiredLabel ? '*' : ''}`}
            </Text>
          </Box>

          {multiline ? (
            <Textarea
              name={formKey}
              required={requiredLabel}
              borderWidth="1px"
              borderRadius={0}
              focusBorderColor="gray.100"
              variant="filled"
              flex={1}
              resize="vertical"
              noOfLines={3}
            />
          ) : (
            <Input
              name={formKey}
              required={requiredLabel}
              type={inputType}
              borderWidth="1px"
              borderRadius={0}
              focusBorderColor="gray.100"
              variant="filled"
              width={{
                base: '100%',
                md: '75%',
              }}
            />
          )}
        </Flex>
      </FormControl>
    ),
    [isMobile, t]
  );

  return (
    <Box
      marginY={{
        base: '20px',
        md: '40px',
      }}
      padding={{
        base: 0,
        md: '12px',
      }}
      width={{
        base: '100%',
        md: '80%',
      }}
      paddingX={{
        base: '20px',
        md: 0,
      }}
    >
      <SectionTitle
        noPadding
        title={t(
          'homepage.contactUs.form.title'
        )}
      />

      <Box
        as="form"
        action={FORMSPREE_ENDPOINT}
        method="POST"
        width="100%"
      >
        <Flex
          width="100%"
          flexDirection={{
            base: 'column',
            md: 'row',
          }}
          flexWrap={{
            base: 'nowrap',
            md: 'wrap',
          }}
          alignItems="center"
          justifyContent="flex-end"
        >
          {formInputs.map((item) =>
            renderFormInput(item)
          )}

          <input
            type="hidden"
            name="_subject"
            value="Website enquiry from arch4D website"
          />

          <input
            type="hidden"
            name="_format"
            value="plain"
          />

          <Box
            width="100%"
            marginY="15px"
          >
            <Checkbox
              size="lg"
              isRequired
              borderColor="gray.200"
              isChecked={checkbox}
              onChange={(e) =>
                setCheckbox(e.target.checked)
              }
              colorScheme="button"
            >
              <Text
                type={{
                  base: TextType.Inter10Light,
                  md: TextType.Inter12Light,
                }}
                color="gray.500"
              >
                {t(
                  'homepage.contactUs.form.checkbox'
                ).replace(
                  '{{privacyPolicy}}',
                  t(
                    'homepage.contactUs.form.privacyPolicy'
                  )
                )}

                <Link
                  to="/legals/privacy-policy"
                  style={{
                    textDecoration: 'underline',
                    marginLeft: 4,
                  }}
                >
                  {t(
                    'homepage.contactUs.form.privacyPolicy'
                  )}
                </Link>
              </Text>
            </Checkbox>
          </Box>

          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
          >
            <Button
              isDisabled={!checkbox}
              bgColor="primary"
              color="white"
              type="submit"
              width={{
                base: '50%',
                md: '33.3333%',
              }}
              borderRadius={0}
            >
              <Text
                type={{
                  base: TextType.Inter14Bold,
                  md: TextType.Inter18Bold,
                }}
                color="white"
              >
                {t(
                  'homepage.contactUs.form.button'
                ).toUpperCase()}
              </Text>
            </Button>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default EmailMeForm;